import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { apiErrorMessage } from '@/lib/apiErrorMessage'

/** Mirrors `@MaxLength` on the server's SkipSubmissionDto. */
const REASON_MAX_LENGTH = 2000

/**
 * The "Skip Form" action (z8nrz7fn5r): why this form is being skipped.
 *
 * The reason is required and goes on the form's audit trail with whoever
 * skipped it. Cancelling sends nothing; a failed skip keeps the dialog open,
 * reason and all, with the server's message.
 */
export function SkipFormDialog({
  open,
  onOpenChange,
  formName,
  onSkip,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  formName?: string
  /** Performs the skip with the trimmed reason; rejects with the server's error. */
  onSkip: (reason: string) => Promise<void>
}) {
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Each skip is its own deliberate act: never prefill the last reason.
  useEffect(() => {
    if (open) {
      setReason('')
      setError(null)
    }
  }, [open])

  const submit = async () => {
    const trimmed = reason.trim()
    if (!trimmed || busy) return
    setBusy(true)
    setError(null)
    try {
      await onSkip(trimmed)
      onOpenChange(false)
    } catch (err) {
      setError(apiErrorMessage(err, 'The form could not be skipped.'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={o => !busy && onOpenChange(o)}>
      <DialogContent className='sm:max-w-md'>
        <DialogHeader>
          <DialogTitle>Skip {formName ? `"${formName}"` : 'this form'}</DialogTitle>
          <DialogDescription>
            The form is closed without being filled, and the run moves on to the next form. The reason is
            recorded in the audit trail. You can unskip it later to fill it.
          </DialogDescription>
        </DialogHeader>
        <div className='space-y-1.5'>
          <Label htmlFor='skip-reason'>
            Reason <span className='text-destructive'>*</span>
          </Label>
          <Textarea
            id='skip-reason'
            rows={3}
            maxLength={REASON_MAX_LENGTH}
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder='Why is this form being skipped?'
            autoFocus
          />
          {error && <p className='text-sm text-destructive'>{error}</p>}
        </div>
        <DialogFooter>
          <Button variant='outline' onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} disabled={busy || reason.trim() === ''}>
            {busy && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
            Skip Form
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
